import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { Box, Loader, Typography } from '@lucid/lego';
import { useStore } from '../../../../../store/store';
import type { Prospect } from '../../../../../api/services/lupp/prospect/dto.prospect';
import type { ProspectTestDrive } from '../../../../../api/services/lupp/prospect/dto.test-drive-schedule';
import { useGetProspectTestDrives } from '../../../../../api/services/lupp/prospect/test-drive-schedule';
import TestDriveList from './TestDriveList';
import TestDriveDetails from './TestDriveDetails';
import ScheduleTestDriveForm from './ScheduleTestDriveForm';
import { isTestDriveModifiable } from './utils';
import { isTestDriveHostAssignmentEnabled } from '../../../../../config/featureFlags';
import styles from './TestDriveTab.module.scss';

type TestDriveTabProps = {
  prospect?: Prospect;
  labels: Record<string, string>;
  onDirtyChange?: (isDirty: boolean) => void;
  /** Accepted for prop parity with the sibling tabs; not invoked for internal list/details navigation. */
  onCancel?: () => void;
};

type ViewState =
  | { mode: 'list' }
  | { mode: 'schedule' }
  | { mode: 'details'; testDriveId: string }
  | { mode: 'modify'; testDriveId: string };

const TestDriveTab: React.FC<TestDriveTabProps> = ({ prospect, labels, onDirtyChange }) => {
  const theme = useStore((state) => state.theme);
  const locale = useStore((state) => state.locale);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [view, setView] = useState<ViewState>({ mode: 'list' });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  // Guards against re-applying the deep link (e.g. `?testDriveId=...`, used by the Admin Test
  // Drive Bookings table) after the rep has navigated elsewhere within this tab.
  const hasAppliedDeepLinkRef = useRef(false);

  useEffect(() => {
    if (!successMessage) return undefined;
    const timeout = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timeout);
  }, [successMessage]);

  const { data: testDrives, isLoading } = useGetProspectTestDrives(
    prospect?.prospectIdentifier ?? '',
    Boolean(prospect?.prospectIdentifier)
  );

  useEffect(() => {
    if (hasAppliedDeepLinkRef.current || !testDrives) return;
    const testDriveId = searchParams.get('testDriveId');
    const linkedTestDrive = testDrives.find(
      (testDrive) => testDrive.testDriveScheduleId === testDriveId
    );
    if (testDriveId && linkedTestDrive) {
      // An unassigned booking opened from the scheduler goes straight to editing so it can be assigned.
      const needsAssignment =
        isTestDriveHostAssignmentEnabled() &&
        searchParams.get('from') === 'scheduler' &&
        !linkedTestDrive.hostUserIdentifier &&
        isTestDriveModifiable(linkedTestDrive);
      setView({ mode: needsAssignment ? 'modify' : 'details', testDriveId });
    }
    hasAppliedDeepLinkRef.current = true;
    if (testDriveId) {
      // Strip `testDriveId` without using `useSearchParams().setSearchParams()`: that helper
      // navigates to a bare `"?search"` string, which drops the current `location.hash` (the
      // `#test-drive` tab anchor), bouncing the slider back to the Details tab. Preserve the
      // pathname and hash explicitly instead.
      const params = new URLSearchParams(searchParams);
      params.delete('testDriveId');
      const query = params.toString();
      navigate(`${location.pathname}${query ? `?${query}` : ''}${location.hash}`, {
        replace: true,
      });
    }
  }, [testDrives, searchParams, location.pathname, location.hash, navigate]);

  const selectedTestDrive = useMemo<ProspectTestDrive | undefined>(() => {
    if (view.mode !== 'details' && view.mode !== 'modify') return undefined;
    return testDrives?.find((testDrive) => testDrive.testDriveScheduleId === view.testDriveId);
  }, [testDrives, view]);

  const goToList = useCallback(() => {
    onDirtyChange?.(false);
    setView({ mode: 'list' });
  }, [onDirtyChange]);

  const handleSaved = useCallback(() => {
    setSuccessMessage(labels?.['testDrive.saveSuccess'] ?? 'Test drive saved successfully.');
    goToList();
  }, [goToList, labels]);

  const handleScheduleNew = useCallback(() => setView({ mode: 'schedule' }), []);

  const handleViewClick = useCallback((record: ProspectTestDrive) => {
    if (!record.testDriveScheduleId) return;
    setView({ mode: 'details', testDriveId: record.testDriveScheduleId });
  }, []);

  const handleModify = useCallback(() => {
    if (view.mode !== 'details') return;
    setView({ mode: 'modify', testDriveId: view.testDriveId });
  }, [view]);

  const handleBackToDetails = useCallback(() => {
    if (view.mode !== 'modify') return;
    onDirtyChange?.(false);
    setView({ mode: 'details', testDriveId: view.testDriveId });
  }, [view, onDirtyChange]);

  if (isLoading) {
    return (
      <Box className={styles.loaderContainer}>
        <Loader />
      </Box>
    );
  }

  const banner = successMessage ? (
    <Box className={styles.successMessageText} pt={12} pb={12} pl={16} pr={16}>
      <Typography variant="body" size="medium" color="white" theme={theme}>
        {successMessage}
      </Typography>
    </Box>
  ) : null;

  if (view.mode === 'schedule') {
    return (
      <>
        {banner}
        <ScheduleTestDriveForm
          prospect={prospect}
          onBack={goToList}
          onSaved={handleSaved}
          onDirtyChange={onDirtyChange}
          labels={labels}
          theme={theme}
        />
      </>
    );
  }

  if (view.mode === 'modify') {
    return (
      <>
        {banner}
        <ScheduleTestDriveForm
          prospect={prospect}
          testDriveSchedule={selectedTestDrive}
          onBack={handleBackToDetails}
          onSaved={handleSaved}
          onBackToList={goToList}
          onDirtyChange={onDirtyChange}
          labels={labels}
          theme={theme}
        />
      </>
    );
  }

  if (view.mode === 'details' && selectedTestDrive) {
    return (
      <>
        {banner}
        <TestDriveDetails
          prospect={prospect}
          testDrive={selectedTestDrive}
          theme={theme}
          labels={labels}
          onBack={goToList}
          onModify={handleModify}
        />
      </>
    );
  }

  return (
    <>
      {banner}
      <TestDriveList
        theme={theme}
        testDrives={testDrives ?? []}
        labels={labels}
        onScheduleNew={handleScheduleNew}
        onViewClick={handleViewClick}
        locale={locale}
      />
    </>
  );
};

export default TestDriveTab;
